import { useFormik } from "formik";
import React, {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { Container, Dialog, Icon, Pressable, Row } from "../components";
import { PartnerLocation } from "../data/location";
import { useI18n } from "../lib/i18n/i18n_provider";
import { isEmail, toDigitsOnly } from "../lib/validation/string";
import {
  OfferingMessageType,
  usePartnerContactFormQuery,
  usePartnerLocationsQuery,
} from "./api";
import { Button } from "./button";
import { Heading } from "./heading";
import { Hidden } from "./hidden";
import { Picker } from "./picker";
import { Spacer } from "./spacer";
import { Text } from "./text";
import { TextArea } from "./text_area";
import { TextFormField } from "./text_form_field";
import { Visible } from "./visible";
import { useAnalytics } from "../lib/analytics/analytics";

interface ContactFormContext {
  onOpenContactForm: () => void;
  talkWithUsExternalUrl?: string;
}

const ContactFormContext = createContext<ContactFormContext>({
  onOpenContactForm: () => {},
});

export function useContactForm() {
  return useContext(ContactFormContext);
}

interface ContactFormProviderProps {
  partnerID: string;
  messageableID: string;
  messageableType: OfferingMessageType;
  hubspotForm?: React.ReactNode;
  talkWithUsExternalUrl?: string;
  children?: React.ReactNode;
}

export function ContactFormProvider(props: ContactFormProviderProps) {
  const {
    messageableID,
    messageableType,
    hubspotForm,
    talkWithUsExternalUrl,
    children,
    partnerID,
  } = props;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<QuestionareData | null>(null);

  return (
    <ContactFormContext.Provider
      value={{ onOpenContactForm: () => setOpen(true), talkWithUsExternalUrl }}
    >
      {children}
      <Dialog isOpen={open} onRequestClose={() => setOpen(false)}>
        <div className="talk-with-us-prompt-dialog-body">
          <Container height="100%">
            <Pressable onPress={() => setOpen(false)}>
              <Icon name="x" />
            </Pressable>
            <Spacer size={16} />
            {hubspotForm ? (
              hubspotForm
            ) : data ? (
              <LetsTalkConfirmation
                data={data}
                onClose={() => {
                  setOpen(false);
                  setData(null);
                }}
              />
            ) : (
              <LetsTalkForm
                partnerID={partnerID}
                messageableID={messageableID}
                messageableType={messageableType}
                onComplete={setData}
              />
            )}
          </Container>
        </div>
      </Dialog>
    </ContactFormContext.Provider>
  );
}

interface LetsTalkFormProps {
  partnerID: string;
  messageableID: string;
  messageableType: OfferingMessageType;
  onComplete: (data: QuestionareData) => void;
}

export function LetsTalkForm(props: LetsTalkFormProps) {
  const { messageableID, onComplete, messageableType, partnerID } = props;
  const { data: partnerContactForm } = usePartnerContactFormQuery(partnerID);
  const locations = usePartnerLocationsQuery(partnerID);

  const initLocation = locations.length === 1 ? locations[0] : null;
  const [selectedLocation, setSelectedLocation] =
    useState<PartnerLocation | null>(initLocation);

  const { fbt } = useI18n();
  const { values, errors, status, submitForm, setFieldValue } = useFormik({
    initialValues: {
      locationID: "",
      firstName: "",
      alreadyHaveReservation: false,
      lastName: "",
      email: "",
      phoneNumber: "",
      message: "",
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate: (v) => {
      const errors: { [field: string]: string } = {};

      if (!v.locationID) {
        errors.locationID = fbt("Please select a location");
      }
      if (!v.firstName) {
        errors.firstName = fbt("Please add your first name");
      }

      if (!v.lastName) {
        errors.lastName = fbt("Please add your last name");
      }

      if (!v.email) {
        errors.email = fbt("Please add your email");
      } else if (!isEmail(v.email)) {
        errors.email = fbt("Please enter a valid email");
      }

      if (!v.message) {
        errors.message = fbt("Message can't be blank");
      }

      return errors;
    },
    onSubmit: (v, { setStatus }) => {
      fetch(
        `/api/v2/offering_message?messageable_type=${messageableType}&messageable_id=${messageableID}&partner_id=${partnerID}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            first_name: v.firstName,
            last_name: v.lastName,
            email: v.email,
            phone_number: v.phoneNumber,
            message: v.message,
            location_id: v.locationID,
            already_have_reservation: v.alreadyHaveReservation,
          }),
        }
      )
        .then(async (result) => {
          if (result.status >= 200 && result.status < 300) {
            onComplete(v);
            return;
          }

          setStatus("Message can't be blank");
        })
        .catch((e) => {
          setStatus("Error submitting form");
          console.error(e.message);
        });
    },
  });
  const analytics = useAnalytics();

  const onChangeLocation = useCallback(
    (locationID: string) => {
      const newSelectedLocation = locations.find((l) => l.id === locationID);
      setSelectedLocation(locations.find((l) => l.id === locationID) || null);
      setFieldValue("locationID", locationID);

      if (!newSelectedLocation) {
        return;
      }

      analytics.event(
        "Let's Talk Location Click",
        {
          Location: newSelectedLocation.name,
          "Location UUID": newSelectedLocation.id,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    },
    [analytics, locations, setFieldValue]
  );

  useEffect(() => {
    if (selectedLocation && selectedLocation?.id !== values.locationID) {
      // This is initial selection when open the pop up
      setFieldValue("locationID", selectedLocation.id);
      analytics.event(
        "Let's Talk Location Click",
        {
          Location: selectedLocation.name,
          "Location UUID": selectedLocation.id,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    }
  }, [analytics, selectedLocation, setFieldValue, values.locationID]);

  const onSubmitForm = useCallback(async () => {
    await submitForm();

    if (!selectedLocation) {
      return;
    }

    analytics.event(
      "Let's Talk Submit Click",
      {
        Location: selectedLocation.name,
        "Location UUID": selectedLocation.id,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics, submitForm, selectedLocation]);

  return (
    <Container flex={1}>
      <Heading>{partnerContactForm?.title || fbt("Let's talk")}</Heading>
      <Text>
        {partnerContactForm?.description ||
          fbt(
            "We would be happy to provide you with more information and answer your specific office needs."
          )}
      </Text>
      <Spacer size={16} />
      <Text color="muted">{fbt("Location")}</Text>
      <Spacer size={4} />
      <LocationPicker
        locations={locations}
        value={values.locationID}
        onChange={onChangeLocation}
      />
      {errors.locationID && (
        <Fragment>
          <Spacer size={4} />
          <Text color="error">{errors.locationID}</Text>
        </Fragment>
      )}
      <Spacer size={16} />
      {partnerContactForm?.alreadyHaveReservationEnabled && (
        <>
          <Text color="muted">{fbt("Already have a reservation")}</Text>
          <Spacer size={4} />
          <AlreadyHaveReservationPicker
            value={values.alreadyHaveReservation}
            onChange={(alreadyHaveReservation) =>
              setFieldValue("alreadyHaveReservation", alreadyHaveReservation)
            }
          />
        </>
      )}

      <Spacer size={16} />
      <TextFormField
        onChange={(firstName) => setFieldValue("firstName", firstName)}
        name="First Name"
        label={fbt("First Name")}
        value={values.firstName}
        error={errors.firstName}
      />
      <Spacer size={8} />
      <TextFormField
        onChange={(lastName) => setFieldValue("lastName", lastName)}
        name="Last Name"
        label={fbt("Last Name")}
        value={values.lastName}
        error={errors.lastName}
      />
      <Spacer size={8} />
      <TextFormField
        onChange={(email) => setFieldValue("email", email)}
        name="Email"
        label={fbt("Email")}
        value={values.email}
        error={errors.email}
      />
      <Spacer size={8} />
      <TextFormField
        onChange={(phoneNumber) =>
          setFieldValue("phoneNumber", toDigitsOnly(phoneNumber))
        }
        name="phoneNumber"
        label={fbt("phoneNumberOptional")}
        value={values.phoneNumber}
        error={errors.phoneNumber}
      />
      <Spacer size={8} />
      <Text color="muted">{fbt("How can we help?")}</Text>
      <TextArea
        rows={5}
        name="message"
        value={values.message}
        onChange={(message) => setFieldValue("message", message)}
      />
      {errors.message && <Text color="error">{errors.message}</Text>}
      <Spacer size={8} />
      {selectedLocation && (
        <Text color="muted" align="right">
          {partnerContactForm?.partnerPhoneBlurb || fbt("Or call us at:")}{" "}
          <a href={`tel:${selectedLocation.contactPhone}`}>
            {selectedLocation.phoneNumberMask ||
              getPhoneLast4Character(selectedLocation.contactPhone)}
          </a>
        </Text>
      )}
      {status && <Text color="error">{status}</Text>}
      <Spacer />
      <Visible md>
        <Row justifyContent="flex-end">
          <Container width={160}>
            <Button title={fbt("Submit")} onPress={onSubmitForm} />
          </Container>
        </Row>
      </Visible>
      <Hidden md>
        <Container>
          <Button title={fbt("Submit")} onPress={submitForm} />
        </Container>
      </Hidden>
    </Container>
  );
}

function getPhoneLast4Character(partnerPhoneNumber: string) {
  const phoneNumber =
    partnerPhoneNumber.length <= 4
      ? partnerPhoneNumber
      : partnerPhoneNumber.slice(partnerPhoneNumber.length - 4);
  return `*${phoneNumber}`;
}

export interface QuestionareData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  message: string;
  alreadyHaveReservation?: boolean;
}

interface LetsTalkConfirmationProps {
  data: QuestionareData;
  onClose: () => void;
}

export function LetsTalkConfirmation(props: LetsTalkConfirmationProps) {
  const { data, onClose } = props;
  const { fbt } = useI18n();
  return (
    <Container flex={1}>
      <Heading>{fbt("Let's talk")}</Heading>
      <Text>
        Thank you {data.firstName}, we will get back to you as soon as we can
        and reply to your email at {data.email}
      </Text>
      <Spacer size={16} />

      <Spacer />
      <Visible md>
        <Row justifyContent="flex-end">
          <Container width={160}>
            <Button title={fbt("Close")} onPress={onClose} />
          </Container>
        </Row>
      </Visible>
      <Hidden md>
        <Container>
          <Button title={fbt("Close")} onPress={onClose} />
        </Container>
      </Hidden>
    </Container>
  );
}

interface LocationPickerProps {
  locations: PartnerLocation[];
  value?: string;
  disabled?: boolean;
  onChange: (locationID: string) => void;
}

function LocationPicker(props: LocationPickerProps) {
  const { disabled, value, locations, onChange } = props;
  const { fbt } = useI18n();

  const options = locations.map((l) => ({
    label: `${l.name} - ${l.streetAddress}`,
    value: l.id,
  }));

  const pickerOverride = {
    root: {
      style: {
        paddingLeft: 4,
        paddingRight: 0,
        paddingTop: 4,
        paddingBottom: 4,
        borderRadius: 7,
        height: "auto",
        border: "1px solid #d7d5dd",
      },
    },
  };

  return (
    <Picker
      icon="map-pin"
      placeholder={fbt("Select a location")}
      value={value}
      options={options}
      onChange={onChange}
      disabled={disabled}
      override={pickerOverride}
    />
  );
}

interface AlreadyHaveReservationPickerProps {
  value?: boolean;
  disabled?: boolean;
  onChange: (alreadyHaveReservation: string) => void;
}

function AlreadyHaveReservationPicker(
  props: AlreadyHaveReservationPickerProps
) {
  const { disabled, value, onChange } = props;
  const { fbt } = useI18n();

  const options = [
    {
      label: fbt("Yes"),
      value: "true",
    },
    {
      label: fbt("No"),
      value: "false",
    },
  ];

  const pickerOverride = {
    root: {
      style: {
        paddingLeft: 10,
        paddingRight: 0,
        paddingTop: 4,
        paddingBottom: 4,
        borderRadius: 7,
        height: "auto",
        border: "1px solid #d7d5dd",
      },
    },
  };

  return (
    <Picker
      value={value?.toString()}
      options={options}
      onChange={onChange}
      disabled={disabled}
      override={pickerOverride}
    />
  );
}
